import React, { useEffect, useRef, useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  Modal,
  Pressable,
  useWindowDimensions,
} from "react-native";
import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import CustomTextInput from "../../../../../components/common/general/CustomTextInput/CustomTextInput";
import CustomButton from "../../../../../components/common/general/CustomButton/CustomButton";
import CommentAvatar from "./CommentAvatar";
import { PaperPlaneTiltIcon } from "@phosphor-icons/react";
import { CircleCheckBig, UserRoundPlusIcon } from "lucide-react";

import { useTeamMembers, useCurrentUserId, useCurrentUserName } from "../contexts/TeamMembersContext";
import type { TeamMember } from "../contexts/TeamMembersContext";

export type { TeamMember };

export interface Comment {
  id: string;
  authorId: string;
  authorName?: string;
  body: string;
  createdAt: string;
  assigneeId?: string | null;
  assigneeName?: string | null;
  resolved?: boolean;
  resolvedById?: string | null;
  resolvedByName?: string | null;
  targetKind?: string;
  targetId?: string;
  targetLabelFallback?: string;
}

export interface CommentThread {
  root: Comment;
  replies: Comment[];
  outdated?: boolean;
}

export type CommentTargetKind = string;

export interface CommentsApi {
  allThreads: () => CommentThread[];
  threadsFor: (kind: CommentTargetKind, id: string) => CommentThread[];
  addComment: (kind: CommentTargetKind, id: string, body: string) => void;
  addReply: (rootId: string, body: string) => void;
  resolve: (rootId: string) => void;
  reopen: (rootId: string) => void;
  assign: (commentId: string, assigneeId: string | null) => void;
  countsFor: (kind: CommentTargetKind, id: string) => { total: number; unresolved: number };
}

function getMember(
  comment: { authorId?: string; authorName?: string } | string,
  teamMembers: TeamMember[],
  fallbackName?: string
): TeamMember {
  if (typeof comment === "string") {
    return (
      teamMembers.find((m) => m.id === comment) ?? { id: comment, name: fallbackName || comment, role: "observer" }
    );
  }
  const found = teamMembers.find((m) => m.id === comment.authorId);
  if (found) return found;
  return {
    id: comment.authorId ?? "",
    name: comment.authorName ?? comment.authorId ?? "Unknown",
    role: "observer",
  };
}

function shortName(name: string): string {
  return name.replace(/^(Dr|Prof|Mr|Ms|Mrs)\.?\s+/i, "").split(" ")[0];
}

function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  return text.slice(0, max - 1).trimEnd() + "…";
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** Renders comment body with @mention tokens highlighted in violet. */
function MentionBody({ body, compact = false }: { body: string; compact?: boolean }) {
  const parts = body.split(/(@[A-Za-z0-9_-]+)/g);
  return (
    <Text style={[styles.commentBody, compact && styles.commentBodyCompact]}>
      {parts.map((part, i) =>
        part.startsWith("@") ? (
          <Text key={i} style={styles.mentionHighlight}>
            {part}
          </Text>
        ) : (
          part
        )
      )}
    </Text>
  );
}

function CommentRow({ comment, compact = false }: { comment: Comment; compact?: boolean }) {
  const teamMembers = useTeamMembers();
  const author = getMember(comment, teamMembers);
  return (
    <View style={styles.commentRow}>
      <CommentAvatar member={author} size={compact ? 28 : 32} />
      <View style={styles.commentRowContent}>
        <View style={styles.commentRowHeader}>
          <CustomText
            text={author.name}
            useTranslationText={false}
            style={[styles.commentAuthor, compact && styles.commentAuthorCompact]}
          />
          <CustomText text={formatTimestamp(comment.createdAt)} useTranslationText={false} style={styles.commentTime} />
        </View>
        <MentionBody body={comment.body} compact={compact} />
      </View>
    </View>
  );
}

function ResolvedThreadStub({
  thread,
  onReopen,
  disabled,
}: {
  thread: CommentThread;
  onReopen: () => void;
  disabled?: boolean;
}) {
  const teamMembers = useTeamMembers();
  const resolver = thread.root.resolvedById
    ? getMember(
        { authorId: thread.root.resolvedById, authorName: thread.root.resolvedByName ?? undefined },
        teamMembers
      )
    : null;
  return (
    <View style={styles.resolvedStub}>
      <View style={styles.resolvedStubLeft}>
        <View style={styles.resolvedIcon}>
          <CustomText text="✓" useTranslationText={false} style={styles.resolvedIconText} />
        </View>
        <CustomText
          text={`Resolved${resolver ? ` by ${resolver.name}` : ""} - "${truncate(thread.root.body, 60)}"`}
          useTranslationText={false}
          style={styles.resolvedText}
          numberOfLines={1}
        />
      </View>
      <CustomButton
        text="↺ Reopen"
        onPress={() => {
          if (disabled) return;
          onReopen();
        }}
        disabled={disabled}
        styleType="transparent"
        textStyle={[styles.ghostButtonText, disabled && styles.ghostButtonTextDisabled]}
        useTranslationText={false}
        style={[styles.ghostButton, disabled && styles.ghostButtonDisabled]}
        leftIcon={undefined}
        rightIcon={undefined}
      />
    </View>
  );
}

function AssignControl({
  comment,
  api,
  disabled,
  onOpenChange,
}: {
  comment: Comment;
  api: CommentsApi;
  disabled?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const teamMembers = useTeamMembers();
  const currentUserId = useCurrentUserId();
  const triggerRef = useRef<View>(null);
  const [open, setOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const assignee = comment.assigneeId
    ? getMember({ authorId: comment.assigneeId, authorName: comment.assigneeName ?? undefined }, teamMembers)
    : null;
  const { width: screenWidth, height: screenHeight } = useWindowDimensions();

  const ASSIGN_MENU_WIDTH = 220;
  const ASSIGN_MENU_ESTIMATED_HEIGHT = assignee ? 220 : 168;

  const dropdownLeft = anchorRect
    ? Math.min(Math.max(8, anchorRect.x + anchorRect.width - ASSIGN_MENU_WIDTH), screenWidth - ASSIGN_MENU_WIDTH - 8)
    : 8;

  const dropdownTop = anchorRect
    ? anchorRect.y + anchorRect.height + 6 + ASSIGN_MENU_ESTIMATED_HEIGHT > screenHeight - 8
      ? Math.max(8, anchorRect.y - ASSIGN_MENU_ESTIMATED_HEIGHT - 6)
      : anchorRect.y + anchorRect.height + 6
    : 8;

  const openMenu = () => {
    if (disabled) return;
    triggerRef.current?.measureInWindow((x: number, y: number, width: number, height: number) => {
      setAnchorRect({ x, y, width, height });
      setOpen(true);
      onOpenChange?.(true);
    });
  };

  const toggleOpen = () => {
    if (open) {
      closeMenu();
      return;
    }

    openMenu();
  };

  const closeMenu = () => {
    setOpen(false);
    onOpenChange?.(false);
  };

  useEffect(() => {
    if (!disabled || !open) return;
    setOpen(false);
    onOpenChange?.(false);
  }, [disabled, onOpenChange, open]);

  return (
    <View style={[styles.assignControl, open && styles.assignControlOpen]}>
      <View ref={triggerRef} collapsable={false}>
        <CustomButton
          text={assignee ? `${shortName(assignee.name)}` : "Assign"}
          onPress={toggleOpen}
          disabled={disabled}
          styleType="transparent"
          textStyle={[
            styles.ghostButtonText,
            assignee && styles.assignButtonActiveText,
            disabled && styles.ghostButtonTextDisabled,
          ]}
          useTranslationText={false}
          style={[styles.ghostButton, assignee && styles.assignButtonActive, disabled && styles.ghostButtonDisabled]}
          leftIcon={
            <UserRoundPlusIcon
              size={14}
              color={disabled ? "#98A2B3" : assignee ? "#7E22CE" : styles.ghostButtonText.color}
              strokeWidth={assignee ? 2.4 : 2}
            />
          }
          leftIconStyle={styles.assignTriggerIcon}
          rightIcon={undefined}
        />
      </View>

      {open && (
        <Modal transparent visible animationType="none" onRequestClose={closeMenu}>
          <Pressable style={styles.assignMenuBackdrop} onPress={closeMenu} />
          <View pointerEvents="box-none" style={styles.assignMenuLayer}>
            <View style={[styles.assignDropdownPortal, { top: dropdownTop, left: dropdownLeft }]}>
              {teamMembers.map((m) => {
                const active = comment.assigneeId === m.id;
                return (
                  <CustomButton
                    key={m.id}
                    text={`${m.name}${m.id === currentUserId ? " (you)" : ""}`}
                    onPress={() => {
                      if (disabled) return;
                      api.assign(comment.id, m.id);
                      closeMenu();
                    }}
                    styleType="transparent"
                    textStyle={styles.assignDropdownName}
                    useTranslationText={false}
                    style={styles.assignDropdownItem}
                    leftIcon={<CommentAvatar member={m} size={18} />}
                    leftIconStyle={styles.assignRowAvatar}
                    rightIcon={
                      active ? <CustomText text="✓" useTranslationText={false} style={styles.assignCheck} /> : undefined
                    }
                  />
                );
              })}
              {assignee && (
                <>
                  <View style={styles.assignSeparator} />
                  <CustomButton
                    text="Unassign"
                    onPress={() => {
                      if (disabled) return;
                      api.assign(comment.id, null);
                      closeMenu();
                    }}
                    styleType="transparent"
                    textStyle={styles.unassignText}
                    useTranslationText={false}
                    style={styles.assignDropdownItem}
                    leftIcon={undefined}
                    rightIcon={undefined}
                  />
                </>
              )}
            </View>
          </View>
        </Modal>
      )}
    </View>
  );
}

function ThreadView({ thread, api, disabled }: { thread: CommentThread; api: CommentsApi; disabled?: boolean }) {
  const { root, replies } = thread;
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyDraft, setReplyDraft] = useState("");
  const [assignMenuOpen, setAssignMenuOpen] = useState(false);

  useEffect(() => {
    if (!disabled) return;
    setReplyOpen(false);
    setAssignMenuOpen(false);
  }, [disabled]);

  if (root.resolved) {
    return <ResolvedThreadStub thread={thread} onReopen={() => api.reopen(root.id)} disabled={disabled} />;
  }

  const handleReply = () => {
    if (disabled) return;
    const body = replyDraft.trim();
    if (!body) return;
    api.addReply(root.id, body);
    setReplyDraft("");
    setReplyOpen(false);
  };

  return (
    <View style={[styles.threadCard, assignMenuOpen && styles.threadCardFloating]}>
      {thread.outdated && (
        <View style={styles.outdatedBadge}>
          <CustomText text="OUTDATED" useTranslationText={false} style={styles.outdatedBadgeText} />
        </View>
      )}
      <CommentRow comment={root} />

      {replies.length > 0 && (
        <View style={styles.repliesContainer}>
          {replies.map((r) => (
            <CommentRow key={r.id} comment={r} compact />
          ))}
        </View>
      )}

      <View style={styles.threadActions}>
        <CustomButton
          text="↩ Reply"
          onPress={() => {
            if (disabled) return;
            setReplyOpen((v) => !v);
          }}
          disabled={disabled}
          styleType="transparent"
          textStyle={[styles.ghostButtonText, disabled && styles.ghostButtonTextDisabled]}
          useTranslationText={false}
          style={[styles.ghostButton, disabled && styles.ghostButtonDisabled]}
          leftIcon={undefined}
          rightIcon={undefined}
        />
        <CustomButton
          text="Resolve"
          onPress={() => {
            if (disabled) return;
            api.resolve(root.id);
          }}
          disabled={disabled}
          styleType="transparent"
          textStyle={[styles.ghostButtonText, styles.resolveButtonText, disabled && styles.ghostButtonTextDisabled]}
          useTranslationText={false}
          style={[styles.ghostButton, disabled && styles.ghostButtonDisabled]}
          leftIcon={
            <CircleCheckBig size={12} color={disabled ? "#98A2B3" : styles.resolveButtonText.color} weight="bold" />
          }
          rightIcon={undefined}
        />
        <AssignControl comment={root} api={api} disabled={disabled} onOpenChange={setAssignMenuOpen} />
      </View>

      {replyOpen && !disabled && (
        <View style={styles.replyInputContainer}>
          <CustomTextInput
            autoFocus
            value={replyDraft}
            onChangeText={setReplyDraft}
            onKeyPress={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                e.preventDefault();
                handleReply();
              }
            }}
            multiline
            numberOfLines={2}
            placeholder="Reply..."
            style={styles.textarea}
          />
          <View style={styles.replyActions}>
            <CustomButton
              text="Cancel"
              onPress={() => {
                setReplyOpen(false);
                setReplyDraft("");
              }}
              styleType="transparent"
              textStyle={styles.ghostButtonText}
              useTranslationText={false}
              style={styles.ghostButton}
              leftIcon={undefined}
              rightIcon={undefined}
            />
            <CustomButton
              text="Reply"
              onPress={handleReply}
              disabled={!replyDraft.trim()}
              styleType="primary"
              size="s"
              textStyle={styles.primaryButtonText}
              useTranslationText={false}
              style={[styles.primaryButton, !replyDraft.trim() && styles.primaryButtonDisabled]}
              leftIcon={undefined}
              rightIcon={undefined}
            />
          </View>
        </View>
      )}
    </View>
  );
}

interface CommentsSectionProps {
  targetKind: CommentTargetKind;
  targetId: string;
  api: CommentsApi;
  contextLabel?: string;
  disabled?: boolean;
  deleted?: boolean;
}

export function CommentsSection({ targetKind, targetId, api, disabled, deleted = false }: CommentsSectionProps) {
  const threads = api.threadsFor(targetKind, targetId);
  const teamMembers = useTeamMembers();
  const currentUserId = useCurrentUserId();
  const currentUserName = useCurrentUserName();
  const me = getMember(currentUserId, teamMembers, currentUserName);
  const [draft, setDraft] = useState("");
  const isReadOnly = Boolean(disabled) || deleted;
  const canPost = !isReadOnly && draft.trim().length > 0;

  const handlePost = () => {
    if (isReadOnly) return;
    const body = draft.trim();
    if (!body) return;
    api.addComment(targetKind, targetId, body);
    setDraft("");
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (isReadOnly) return;
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      handlePost();
    }
  };

  return (
    <KeyboardAvoidingView behavior={Platform.OS === "ios" ? "padding" : undefined} style={styles.section}>
      {deleted && (
        <View style={styles.deletedBanner}>
          <View style={styles.deletedTag}>
            <CustomText text="Deleted" useTranslationText={false} style={styles.deletedTagText} />
          </View>
          <CustomText
            text="This item has been deleted. Comments are read-only."
            useTranslationText={false}
            style={styles.deletedBannerText}
          />
        </View>
      )}
      {threads.length === 0 ? (
        <CustomText
          text={
            deleted
              ? "No comments on this deleted item."
              : disabled
                ? "No comments yet. Comments are read-only in this view."
                : "No comments yet. Start a discussion with your team."
          }
          useTranslationText={false}
          style={styles.emptyText}
        />
      ) : (
        <View style={styles.threadList}>
          {threads.map((thread) => (
            <ThreadView key={thread.root.id} thread={thread} api={api} disabled={isReadOnly} />
          ))}
        </View>
      )}

      {!deleted && (
        <View style={styles.newCommentRow}>
          <CommentAvatar member={me} size={32} />
          <View style={styles.newCommentInputWrap}>
            <CustomTextInput
              value={draft}
              onChangeText={setDraft}
              onKeyPress={handleKeyPress}
              multiline
              numberOfLines={2}
              placeholder="Add a comment..."
              editable={!isReadOnly}
              style={[styles.textarea, isReadOnly && styles.textareaDisabled]}
            />
            <View style={styles.postActions}>
              {!isReadOnly && <CustomText text="⌘↵ to post" useTranslationText={false} style={styles.hintText} />}
              <CustomButton
                text="Comment"
                onPress={handlePost}
                disabled={!canPost}
                styleType="primary"
                size="s"
                textStyle={styles.primaryButtonText}
                useTranslationText={false}
                style={[styles.primaryButton, !canPost && styles.primaryButtonDisabled]}
                leftIcon={<PaperPlaneTiltIcon size={14} color={styles.primaryButtonText.color} />}
                rightIcon={undefined}
              />
            </View>
          </View>
        </View>
      )}
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  section: {
    flex: 1,
  },
  emptyText: {
    fontSize: 14,
    color: "#667085",
    marginBottom: 16,
  },
  threadList: {
    gap: 12,
    marginBottom: 16,
  },

  commentRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
  },
  commentRowContent: {
    flex: 1,
    minWidth: 0,
  },
  commentRowHeader: {
    flexDirection: "row",
    alignItems: "baseline",
    gap: 8,
    flexWrap: "wrap",
  },
  commentAuthor: {
    fontSize: 14,
    fontWeight: "600",
    color: "#101828",
  },
  commentAuthorCompact: {
    fontSize: 12,
  },
  commentTime: {
    fontSize: 12,
    color: "#667085",
  },
  commentBody: {
    fontSize: 14,
    color: "#344054",
    lineHeight: 20,
    marginTop: 2,
  },
  commentBodyCompact: {
    fontSize: 12,
    lineHeight: 18,
  },

  threadCard: {
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 8,
    padding: 12,
    position: "relative",
    overflow: "visible",
  },
  threadCardFloating: {
    zIndex: 20,
  },
  repliesContainer: {
    marginTop: 12,
    marginLeft: 44,
    paddingLeft: 12,
    borderLeftWidth: 2,
    borderLeftColor: "#F3F4F6",
    gap: 12,
  },
  threadActions: {
    marginTop: 12,
    marginLeft: 44,
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    flexWrap: "wrap",
    overflow: "visible",
  },

  replyInputContainer: {
    marginTop: 8,
    marginLeft: 44,
  },
  replyActions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 8,
    marginTop: 8,
  },

  resolvedStub: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#F9FAFB",
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  resolvedStubLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flex: 1,
    minWidth: 0,
  },
  resolvedIcon: {
    width: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: "#F2FCEB",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  resolvedIconText: {
    fontSize: 10,
    color: "#4ba30d",
    fontWeight: "700",
    lineHeight: 10,
  },
  resolvedText: {
    fontSize: 12,
    color: "#667085",
    flex: 1,
  },
  ghostButton: {
    height: 28,
    paddingHorizontal: 8,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  ghostButtonDisabled: {
    opacity: 0.55,
  },
  ghostButtonText: {
    fontSize: 12,
    color: "#475467",
    fontWeight: "500",
  },
  ghostButtonTextDisabled: {
    color: "#98A2B3",
  },
  resolveButtonText: {
    color: "#475467",
  },

  assignButtonActive: {
    backgroundColor: "#EDE9FE",
  },
  assignButtonActiveText: {
    color: "#6D28D9",
  },
  assignControl: {
    position: "relative",
    zIndex: 2,
  },
  assignControlOpen: {
    zIndex: 40,
  },
  assignTriggerIcon: {
    marginRight: 4,
  },
  assignMenuBackdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "transparent",
  },
  assignMenuLayer: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 999,
  },
  assignDropdownPortal: {
    position: "absolute",
    width: 220,
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 8,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 8,
    zIndex: 1000,
    overflow: "hidden",
  },
  assignDropdownItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  assignRowAvatar: {
    marginRight: 8,
  },
  assignDropdownName: {
    fontSize: 14,
    color: "#101828",
  },
  assignCheck: {
    fontSize: 13,
    color: "#7C3AED",
    fontWeight: "700",
    flexShrink: 0,
  },
  assignSeparator: {
    height: 1,
    backgroundColor: "#E5E7EB",
    marginVertical: 4,
  },
  unassignText: {
    fontSize: 14,
    color: "#667085",
  },

  textarea: {
    width: "100%",
    fontSize: 14,
    color: "#101828",
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    textAlignVertical: "top",
    minHeight: 60,
  },
  textareaDisabled: {
    backgroundColor: "#F9FAFB",
    color: "#98A2B3",
  },

  newCommentRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
  },
  newCommentInputWrap: {
    flex: 1,
    minWidth: 0,
  },
  postActions: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 12,
    marginTop: 8,
  },
  hintText: {
    fontSize: 11,
    color: "#667085",
  },

  primaryButton: {
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
  },
  primaryButtonDisabled: {
    backgroundColor: "#DDD6FE",
  },
  primaryButtonText: {
    fontSize: 12,
    color: "#FFFFFF",
    fontWeight: "500",
  },

  mentionHighlight: {
    color: "#7E22CE",
    fontWeight: "600",
  },

  outdatedBadge: {
    alignSelf: "flex-start",
    backgroundColor: "#FEF3C7",
    borderWidth: 1,
    borderColor: "#FDE68A",
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
    marginBottom: 6,
  },
  outdatedBadgeText: {
    fontSize: 10,
    fontWeight: "700",
    color: "#92400E",
    letterSpacing: 0.6,
  },

  deletedBanner: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    backgroundColor: "#FFF7ED",
    borderWidth: 1,
    borderColor: "#FED7AA",
    borderRadius: 6,
    paddingVertical: 8,
    paddingHorizontal: 12,
    marginBottom: 12,
  },
  deletedTag: {
    backgroundColor: "#FEE2E2",
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
    flexShrink: 0,
  },
  deletedTagText: {
    fontSize: 11,
    fontWeight: "600",
    color: "#B91C1C",
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  deletedBannerText: {
    fontSize: 13,
    color: "#92400E",
    flex: 1,
  },
});
